import { useTranslation } from 'react-i18next';
import { IGenericBookingFields } from '../hooks/useCart';

function ContactFields({
    values,
    setValues,
    emailError = false,
}: Readonly<{
    values: IGenericBookingFields;
    setValues: (bookingFields: IGenericBookingFields) => void;
    emailError?: boolean;
}>) {
    const { t } = useTranslation();
    const showEmailError = emailError && values.email.length > 0;

    // Accept only characters that appear in real phone numbers: digits,
    // spaces, parens, dashes, and a plus. Letters or @ signs get surfaced
    // as an error so guests can't paste their email / free-form text into
    // the phone field and later crash Stripe on customers.create.
    const phoneAllowedRe = /^[\d+\-() ]*$/;
    const phoneValue = values.phone;
    const phoneHasDisallowedChars = phoneValue.length > 0 && !phoneAllowedRe.test(phoneValue);
    const phoneHasNoDigits = phoneValue.length > 0 && !/\d/.test(phoneValue);
    const showPhoneError = phoneHasDisallowedChars || phoneHasNoDigits;

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        e.preventDefault();
        let next = e.target.value;
        if (e.target.name === 'phone') {
            // Strip disallowed characters on input so pasting an email or
            // typing letters just disappears — matches the backend
            // validateBookingPhone normalization.
            next = next.replace(/[^\d+\-() ]/g, '').slice(0, 20);
        }
        setValues({
            ...values,
            [e.target.name]: next,
        });
    };
    return (
        <div className="fireknife-form-groups fireknife-form-groups--contact-info">
            <div className="fireknife-form-group">
                <label className="fireknife-form-label fireknife-form-label--required">
                    {t('widgetBookingInformation.email')}
                </label>
                <div
                    className={`fireknife-form-control-box ${
                        showEmailError ? 'fireknife-form-control-box--error' : ''
                    }`}
                >
                    <input
                        className="fireknife-form-control"
                        type="email"
                        name="email"
                        value={values.email}
                        onChange={handleChange}
                        required
                    />
                </div>
                {showEmailError && (
                    <div className="fireknife-form-control-box--error-message">
                        {t('widgetBookingInformation.emailError')}
                    </div>
                )}
            </div>

            <div className="fireknife-form-group">
                <label className="fireknife-form-label fireknife-form-label--required">
                    {t('widgetBookingInformation.phone')}
                </label>
                <div
                    className={`fireknife-form-control-box ${
                        showPhoneError ? 'fireknife-form-control-box--error' : ''
                    }`}
                >
                    <input
                        className="fireknife-form-control"
                        type="tel"
                        name="phone"
                        inputMode="tel"
                        pattern="[\d+\-\(\) ]{5,20}"
                        maxLength={20}
                        value={values.phone}
                        onChange={handleChange}
                        required
                    />
                </div>
                {showPhoneError && (
                    <div className="fireknife-form-control-box--error-message">
                        {t('widgetBookingInformation.phoneError')}
                    </div>
                )}
            </div>

            <div className="fireknife-form-group">
                <label className="fireknife-form-label fireknife-form-label--required">
                    {t('widgetBookingInformation.firstName')}
                </label>
                <div className="fireknife-form-control-box">
                    <input
                        className="fireknife-form-control"
                        type="text"
                        name="firstName"
                        value={values.firstName}
                        onChange={handleChange}
                        required
                    />
                </div>
            </div>

            <div className="fireknife-form-group">
                <label className="fireknife-form-label fireknife-form-label--required">
                    {t('widgetBookingInformation.lastName')}
                </label>
                <div className="fireknife-form-control-box">
                    <input
                        className="fireknife-form-control"
                        type="text"
                        name="lastName"
                        value={values.lastName}
                        onChange={handleChange}
                        required
                    />
                </div>
            </div>
        </div>
    );
}

export default ContactFields;
